import { AbsgArrayHelper } from '@absg/angular-common';
import { AssignedProductDetail, MedicalEquivalentApi, ProductDetail } from '@absg/api-formulary';
import { InventoryItemSummary } from '@absg/api-inventory';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { concatMap, map, take } from 'rxjs/operators';
import { SettingsService } from 'shared.root/services/settings.service';

import { StationInfoService } from './station-info.service';

@Injectable({ providedIn: 'root' })
export class ManagePositionsCoreService {

  constructor(
    private stationInfoService: StationInfoService,
    private settingsService: SettingsService,
    private medicalEquivalentApi: MedicalEquivalentApi,
  ) { }

  /***
   * Returns the preferred Item productDetail if there is one
   * Or it will return the productDetail for the  cheapest item not found elsewhere on the station if no preferred
   */
  getAutoReorderItemIdForTransactionRequest(containerId: number,
    productId: number, productDetails: AssignedProductDetail[], toExclude?: number[], toInclude?: number[],
  ): Observable<AssignedProductDetail> {
    // Has a preferred Item
    const preferredItemId = this.settingsService.getPreferredItemIdSetting(containerId);
    if (!!preferredItemId) {
      const productDetail: AssignedProductDetail = AbsgArrayHelper.find(productDetails, (product) => product.itemId === preferredItemId);
      if (!!productDetail) {
        return of(productDetail);
      }
    }

    return this.getAutoReorderItemDetail(containerId, productId, toExclude, toInclude);
  }

  getPreferredAutoReorderItemIdForTransactionRequest(containerId: number, productDetails: AssignedProductDetail[]): AssignedProductDetail {
    // Has a preferred Item
    const preferredItemId = this.settingsService.getPreferredItemIdSetting(containerId);
    if (preferredItemId) {
      const productDetail: AssignedProductDetail = AbsgArrayHelper.find(productDetails, (product) => product.itemId === preferredItemId);
      if (!AbsgArrayHelper.isNil(productDetail)) {
        return productDetail;
      }
    }
    return null;
  }

  private getAutoReorderItemDetail(containerId: number,
    productId: number, toExclude: number[], toInclude: number[],
  ): Observable<AssignedProductDetail> {
    return this.stationInfoService.getCachedInventoryItems().pipe(take(1),
      concatMap((allInventoryItems) => {
        const nonPatientOwnedItems = AbsgArrayHelper.filter(allInventoryItems, i => AbsgArrayHelper.isNil(i.patientId));
        // Do not include any items assigned elsewhere in the station
        let excludeList = this.getSupplierProductInclusionIds(
          AbsgArrayHelper.filter(nonPatientOwnedItems, i => i.positionContainerId !== containerId));
        if (!!toExclude) {
          excludeList = AbsgArrayHelper.union(excludeList, toExclude);
        }
        if (!!toInclude) {
          excludeList = AbsgArrayHelper.difference(excludeList, toInclude);
        }
        const request = this.stationInfoService.generateGetCheapestEquivilantRequest(excludeList);
        return this.medicalEquivalentApi.getCheapestEquivilantForProduct(productId, request).pipe(take(1),
          map((product: ProductDetail) => ({
            supplierProductInclusionId: product.supplierProductInclusionId,
            itemId: product.itemId,
            tradeName: product.tradeName,
            dFactor: product.dispenseUnitOfMeasureConversionFactor,
            iFactor: product.inventoryUnitOfMeasureConversionFactor,
            pFactor: product.purchaseUnitOfMeasureConversionFactor,
            meiParentId: product.meiParentId,
            formularyTypeId: product.formularyTypeId,
          } as AssignedProductDetail)));
      }));
  }

  getSupplierProductInclusionIds(inventoryItems: InventoryItemSummary[]): number[] {
    if (!inventoryItems) {
      return [];
    }
    const ids = inventoryItems
      .filter(inventoryItem => inventoryItem?.supplierProductInclusionId)
      .map(inventoryItem => inventoryItem.supplierProductInclusionId);

    return ids;
  }

  getPreferredItemSetting(containerId: number, itemId: string): boolean {
    const retValue = this.settingsService.getPreferredItemIdSetting(containerId);
    if (!AbsgArrayHelper.isNil(retValue)) {
      return (retValue === itemId);
    }
    return false;
  }
}
